/**
 * Contextual affiliate bandit: Thompson sampling per (surface, context bucket)
 * with shrinkage to the surface, then to the arm's global stats, times a
 * deterministic intent-pool fit. The `affiliate-context-bandit` PostHog flag
 * is the sole switch (50/50 A/B against the current engine); see the offer
 * route for where the `test` arm swaps this in as the ranker.
 *
 * Why (owner, 2026-10-30): "we need to make money at all cost (aslo ads
 * affilate personalization relevanncy anything we can improve)". The current
 * engine learns one CTR per arm across every surface and context, so Fiverr
 * competes on equal terms with Wise for a visitor who just applied to a dev job.
 *
 * Context, all already on the request or in the browser (no PII, no AI, no
 * network):
 *   - intent stage: browsing the feed, on a job page, just applied, reading
 *     content. From the surface plus the client's `cx_funnel` / `cx_page`.
 *   - category group: the request's job category, else the last category the
 *     visitor viewed (`cx_cat`, localStorage).
 *   - geo: CF country (ID vs everyone else).
 *
 * The bucket is `intent|catGroup|geo`: 4 x 3 x 2 = 24 cells per surface. At
 * ~10k human impressions a month most cells are sparse, which is what the
 * shrinkage is for: a cell's prior is its surface's posterior mean worth
 * `PRIOR_STRENGTH` pseudo-impressions, and the surface's prior is the arm's
 * global posterior mean. An empty cell therefore samples like the surface, and
 * an empty surface like today's global engine.
 *
 * Bucket counts come from `ad_affiliate_events.slot` = `cb:<bucket>` on
 * affiliate impressions and clicks (the column is otherwise only used by
 * display-ad rows), so no migration is needed.
 *
 * Dependency-light on purpose: the client imports the constants and the
 * bucket helpers, so nothing here may pull zod or a server module.
 */

import type { ArmConfig, MonetizationSettings } from "./settings";

export const CONTEXT_BANDIT_FLAG = "affiliate-context-bandit";
/** Query param carrying the visitor's flag arm to the offer route. */
export const BANDIT_ARM_PARAM = "cx_bandit";
/** Query param carrying the last job category the visitor viewed. */
export const CATEGORY_HINT_PARAM = "cx_cat";
/** Prefix of `ad_affiliate_events.slot` on affiliate rows that carry a bucket. */
export const BUCKET_SLOT_PREFIX = "cb:";
/** Pseudo-impressions a parent level's mean is worth as a child's prior. */
export const PRIOR_STRENGTH = 50;

export type IntentStage = "browse" | "job" | "applied" | "content";
export type CategoryGroup = "tech" | "nontech" | "none";
export type GeoGroup = "id" | "intl";

const INTENTS: readonly IntentStage[] = ["browse", "job", "applied", "content"];
const CAT_GROUPS: readonly CategoryGroup[] = ["tech", "nontech", "none"];
const GEOS: readonly GeoGroup[] = ["id", "intl"];

/** Surfaces rendered right after an apply click. */
const APPLIED_SURFACES = new Set([
	"post_apply",
	"job_detail_after_apply",
	"apply_page",
]);
/** Surfaces that only render on a job detail page. */
const JOB_SURFACES = new Set(["job_sidebar"]);
/** Content pages: blog, guides, salary pages. */
const CONTENT_SURFACES = new Set([
	"blog_top",
	"blog_bottom",
	"blog_post",
	"blog_index",
	"blog_educative",
	"blog_deel_guide",
	"collaboration_post",
	"salary_guide",
	"resume_example_sidebar",
	"cover_letter_sidebar",
	"career_guide_sidebar",
	"interview_questions_sidebar",
]);

/**
 * Intent stage. A recent apply wins over the page: someone who applied in the
 * last 24 h and is back on the feed is still in the "get paid / get ready"
 * frame of mind.
 */
export function intentStageOf(input: {
	surface: string;
	funnel?: string | null;
	pageFamily?: string | null;
}): IntentStage {
	const base = input.surface.split(":")[0];
	if (APPLIED_SURFACES.has(base) || input.funnel === "applied_today")
		return "applied";
	if (JOB_SURFACES.has(base) || input.pageFamily === "job") return "job";
	if (CONTENT_SURFACES.has(base) || input.pageFamily === "blog")
		return "content";
	return "browse";
}

const TECH_CATEGORIES = new Set([
	"engineering",
	"ai",
	"data-analytics",
	"product",
	"design",
	"cat_eng",
	"cat_ai",
	"cat_data",
	"cat_product",
	"cat_design",
]);

/** Category slug or id → coarse group. Unknown non-empty values are nontech. */
export function categoryGroupOf(
	category: string | null | undefined,
): CategoryGroup {
	const c = category?.trim();
	if (!c) return "none";
	return TECH_CATEGORIES.has(c) ? "tech" : "nontech";
}

export function geoGroupOf(country: string | null | undefined): GeoGroup {
	const cc = country?.toUpperCase().trim();
	// Unknown country maps to ID, same as `adGeoFromCountry`: the audience is
	// overwhelmingly Indonesian.
	if (!cc || cc === "ID" || cc === "XX" || cc === "T1") return "id";
	return "intl";
}

export type ContextBucket = {
	intent: IntentStage;
	catGroup: CategoryGroup;
	geo: GeoGroup;
};

export function bucketKey(b: ContextBucket): string {
	return `${b.intent}|${b.catGroup}|${b.geo}`;
}

/** Inverse of `bucketKey`; null for anything outside the closed vocabulary. */
export function parseBucketKey(
	key: string | null | undefined,
): ContextBucket | null {
	if (!key) return null;
	const [intent, catGroup, geo, extra] = key.split("|");
	if (extra !== undefined) return null;
	if (
		!INTENTS.includes(intent as IntentStage) ||
		!CAT_GROUPS.includes(catGroup as CategoryGroup) ||
		!GEOS.includes(geo as GeoGroup)
	)
		return null;
	return {
		intent: intent as IntentStage,
		catGroup: catGroup as CategoryGroup,
		geo: geo as GeoGroup,
	};
}

/** `cb:<bucket>` for the D1 slot column, or undefined for a non-bucket value. */
export function bucketSlot(key: string | null | undefined): string | undefined {
	return parseBucketKey(key) ? `${BUCKET_SLOT_PREFIX}${key}` : undefined;
}

/* ------------------------------------------------------------------------- *
 * Offer pools by intent
 * ------------------------------------------------------------------------- */

export type OfferPool =
	| "get_paid" // receive USD pay from abroad: Wise, Payoneer, Deel, Remote
	| "apply_tools" // CV, application tracking, interview prep
	| "skills" // courses and English
	| "gear" // remote-work equipment
	| "freelance" // gig marketplaces
	| "consumer" // paylater, eSIM, shopping
	| "neutral";

/**
 * Pool per arm slug. Unknown slugs (a new `affiliate_offers` row) are
 * `neutral` (weight 1) until listed, so a new partner is never down-weighted
 * by omission. Classification only: which partner runs, where, and at what
 * payout stays in the catalog rows and the `monetization-settings` payload.
 */
export const ARM_POOLS: Readonly<Record<string, OfferPool>> = {
	wise: "get_paid",
	payoneer: "get_paid",
	deel: "get_paid",
	remote: "get_paid",
	huntr: "apply_tools",
	visualcv: "apply_tools",
	educative: "skills",
	italki: "skills",
	shopee: "gear",
	fiverr: "freelance",
	contra: "freelance",
	allo_paylater: "consumer",
	megaesim: "consumer",
	tokopedia: "consumer",
	tiktok_shop: "consumer",
	blibli: "consumer",
	traveloka: "consumer",
	nordvpn: "consumer",
};

/**
 * Multiplier per (pool, intent). On a job page or after an apply the visitor
 * is a would-be remote USD earner: tools that get them paid or hired go up,
 * context-blind offers (a gig marketplace to someone applying for a salaried
 * role, a paylater to someone who just applied) go down. Browsing and content
 * stay close to neutral so the learned CTR decides.
 */
const POOL_INTENT_WEIGHT: Record<OfferPool, Record<IntentStage, number>> = {
	get_paid: { browse: 1.0, job: 1.4, applied: 1.6, content: 1.0 },
	apply_tools: { browse: 1.1, job: 1.4, applied: 1.3, content: 1.1 },
	skills: { browse: 1.0, job: 1.2, applied: 1.3, content: 1.2 },
	gear: { browse: 1.0, job: 1.0, applied: 1.1, content: 1.0 },
	freelance: { browse: 1.0, job: 0.6, applied: 0.5, content: 1.0 },
	consumer: { browse: 1.0, job: 0.7, applied: 0.6, content: 0.8 },
	neutral: { browse: 1.0, job: 1.0, applied: 1.0, content: 1.0 },
};

export function poolOf(slug: string): OfferPool {
	return ARM_POOLS[slug] ?? "neutral";
}

/** Deterministic relevance multiplier for an arm in a bucket. */
export function poolFit(slug: string, bucket: ContextBucket): number {
	const pool = poolOf(slug);
	let w = POOL_INTENT_WEIGHT[pool][bucket.intent];
	// Tech courses fit tech seekers; a tech seeker is less likely to want gigs.
	if (pool === "skills" && bucket.catGroup === "tech") w *= 1.2;
	if (
		pool === "freelance" &&
		bucket.catGroup === "tech" &&
		bucket.intent !== "browse"
	)
		w *= 0.8;
	return w;
}

/* ------------------------------------------------------------------------- *
 * Hierarchical Thompson sampling
 * ------------------------------------------------------------------------- */

export type ArmCounts = { impressions: number; clicks: number };

/** Counts keyed `surface` → `platform` and `surface|bucket` → `platform`. */
export type BanditStats = {
	surface: Map<string, Map<string, ArmCounts>>;
	bucket: Map<string, Map<string, ArmCounts>>;
};

export type BanditStatRow = {
	surface: string;
	slot: string | null;
	platform: string;
	impressions: number;
	clicks: number;
};

function addCounts(
	map: Map<string, Map<string, ArmCounts>>,
	key: string,
	platform: string,
	impressions: number,
	clicks: number,
): void {
	let inner = map.get(key);
	if (!inner) {
		inner = new Map();
		map.set(key, inner);
	}
	const prev = inner.get(platform) ?? { impressions: 0, clicks: 0 };
	inner.set(platform, {
		impressions: prev.impressions + impressions,
		clicks: prev.clicks + clicks,
	});
}

/** Fold D1 rows (surface, slot, platform) into surface and bucket counts. */
export function buildBanditStats(rows: readonly BanditStatRow[]): BanditStats {
	const stats: BanditStats = { surface: new Map(), bucket: new Map() };
	for (const r of rows) {
		const surface = r.surface.split(":")[0];
		addCounts(stats.surface, surface, r.platform, r.impressions, r.clicks);
		const key = r.slot?.startsWith(BUCKET_SLOT_PREFIX)
			? r.slot.slice(BUCKET_SLOT_PREFIX.length)
			: null;
		if (key && parseBucketKey(key)) {
			addCounts(
				stats.bucket,
				`${surface}|${key}`,
				r.platform,
				r.impressions,
				r.clicks,
			);
		}
	}
	return stats;
}

/**
 * Posterior Beta(α, β) for an arm in one (surface, bucket) cell, shrunk level
 * by level: global (ctrPrior + global counts) → surface → bucket. Each child
 * level starts from its parent's posterior mean worth `strength` impressions.
 */
export function shrunkPosterior(input: {
	prior: { alpha: number; beta: number };
	global?: ArmCounts;
	surface?: ArmCounts;
	bucket?: ArmCounts;
	strength?: number;
}): { alpha: number; beta: number; level: "bucket" | "surface" | "global" } {
	const k = input.strength ?? PRIOR_STRENGTH;
	const g = input.global ?? { impressions: 0, clicks: 0 };
	const a0 = input.prior.alpha + g.clicks;
	const b0 = input.prior.beta + Math.max(0, g.impressions - g.clicks);
	const m0 = a0 / (a0 + b0);
	const s = input.surface ?? { impressions: 0, clicks: 0 };
	const a1 = k * m0 + s.clicks;
	const b1 = k * (1 - m0) + Math.max(0, s.impressions - s.clicks);
	const m1 = a1 / (a1 + b1);
	const c = input.bucket ?? { impressions: 0, clicks: 0 };
	const a2 = k * m1 + c.clicks;
	const b2 = k * (1 - m1) + Math.max(0, c.impressions - c.clicks);
	const level =
		c.impressions > 0 ? "bucket" : s.impressions > 0 ? "surface" : "global";
	return { alpha: a2, beta: b2, level };
}

export type BanditScoredArm = {
	partner: string;
	arm: ArmConfig;
	eEPM: number;
	pClick: number;
	reason: "ctr" | "prior";
	fit: number;
	level: "bucket" | "surface" | "global";
};

/**
 * Rank eligible arms by sampled eEPM × pool fit × optional relevance. The
 * caller passes the eligibility check (`isArmEligible`) and the Beta sampler
 * so this module stays free of the ranker's imports and is deterministic
 * under an injected RNG.
 */
export function sampleContextBandit(
	settings: MonetizationSettings,
	input: {
		surface: string;
		bucket: ContextBucket;
		globalStats: Map<string, ArmCounts>;
		banditStats: BanditStats;
		relevance?: Map<string, number>;
		isEligible: (partner: string, arm: ArmConfig) => boolean;
		effectiveEpc: (arm: ArmConfig) => number;
		betaSample: (a: number, b: number, rng: () => number) => number;
		rng?: () => number;
	},
): BanditScoredArm[] {
	const rng = input.rng ?? Math.random;
	const { ctrPrior, minImpressionsForCtr, explorationRate } = settings.engine;
	const surface = input.surface.split(":")[0];
	const surfaceCounts = input.banditStats.surface.get(surface);
	const bucketCounts = input.banditStats.bucket.get(
		`${surface}|${bucketKey(input.bucket)}`,
	);
	const scored: BanditScoredArm[] = [];
	for (const [partner, arm] of Object.entries(settings.payouts)) {
		if (!input.isEligible(partner, arm)) continue;
		const fit = poolFit(partner, input.bucket);
		const weight = (input.relevance?.get(partner) ?? 1) * fit;
		if (arm.payoutModel === "ecpm") {
			scored.push({
				partner,
				arm,
				eEPM: arm.declaredValue * weight,
				pClick: 0,
				reason: "prior",
				fit,
				level: "global",
			});
			continue;
		}
		const global = input.globalStats.get(partner);
		const post = shrunkPosterior({
			prior: ctrPrior,
			global,
			surface: surfaceCounts?.get(partner),
			bucket: bucketCounts?.get(partner),
		});
		const pClick = input.betaSample(post.alpha, post.beta, rng);
		scored.push({
			partner,
			arm,
			eEPM: pClick * input.effectiveEpc(arm) * 1000 * weight,
			pClick,
			reason:
				(global?.impressions ?? 0) >= minImpressionsForCtr ? "ctr" : "prior",
			fit,
			level: post.level,
		});
	}
	if (explorationRate > 0 && rng() < explorationRate) {
		for (let i = scored.length - 1; i > 0; i--) {
			const j = Math.floor(rng() * (i + 1));
			[scored[i], scored[j]] = [scored[j], scored[i]];
		}
		return scored;
	}
	return scored.sort(
		(a, b) => b.eEPM - a.eEPM || a.partner.localeCompare(b.partner),
	);
}
